import React from "react";
import { BarChart2, TrendingDown, TrendingUp } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export type MetricValueStatus = "good" | "warning" | "bad" | "neutral";

const valueStatusClass: Record<MetricValueStatus, string> = {
  good: "text-[#0C3451] dark:text-white",
  warning: "text-red-600 dark:text-red-400",
  bad: "text-red-600 dark:text-red-400",
  neutral: "text-[#0C3451] dark:text-white",
};

function parseTrendNumber(trend: string): number | null {
  const cleaned = trend.replace(/%/g, "").trim();
  const num = parseFloat(cleaned);
  return Number.isFinite(num) ? num : null;
}

interface CallQualityMetricCardProps {
  title: string;
  value: string;
  valueStatus?: MetricValueStatus;
  trend?: string;
  target?: string;
  icon?: React.ReactNode;
  iconTooltip?: string;
  loading?: boolean;
  onClick?: () => void;
}

const CallQualityMetricCard: React.FC<CallQualityMetricCardProps> = ({
  title,
  value,
  valueStatus = "neutral",
  trend,
  target,
  icon,
  iconTooltip,
  loading,
  onClick,
}) => {
  const clickable = typeof onClick === "function";
  const displayIcon = icon ?? <BarChart2 className="h-5 w-5 text-teamscx-primary dark:text-white" />;
  const tooltipText = iconTooltip ?? title;

  return (
    <div
      className={cn(
        "w-full min-w-[200px] h-[110px] rounded-lg border flex flex-col bg-white dark:bg-[#0C3451] dark:border-[#3C5C73] p-3 transition-colors",
        clickable && !loading && "cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1a4a66]"
      )}
      onClick={loading ? undefined : onClick}
    >
      <div className="flex flex-row items-center justify-between flex-shrink-0 mb-0.5">
        <div className="text-[14px] font-[600] text-teamscx-primary dark:text-white truncate">
          {title}
        </div>
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <span className="flex-shrink-0 [&_svg]:w-5 [&_svg]:h-5 text-teamscx-primary dark:text-white cursor-default">
                {displayIcon}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p className="text-xs">{tooltipText}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      {loading ? (
        <Skeleton className="flex-1 min-h-0 rounded-md" />
      ) : (
        <div className="flex-1 flex flex-col justify-center min-h-0 gap-1.5">
          <div className={cn("text-[20px] font-[700] leading-tight", valueStatusClass[valueStatus])}>
            {value}
          </div>
          <div className="flex flex-col gap-0.5">
            {trend != null && trend !== "" && (() => {
              const trendNum = parseTrendNumber(trend);
              const isPositive = trendNum !== null && trendNum > 0;
              const isNegative = trendNum !== null && trendNum < 0;
              const trendColorClass = isPositive
                ? "text-green-600"
                : isNegative
                  ? "text-red-600"
                  : "text-gray-600 dark:text-white";
              const TrendIcon = isNegative ? TrendingDown : TrendingUp;
              return (
                <div className={cn("text-[12px] font-[500] flex items-center gap-1", trendColorClass)}>
                  <TrendIcon className="w-3.5 h-3.5 flex-shrink-0" aria-hidden />
            <span>{trend}</span>
                </div>
              );
            })()}
            {target != null && target !== "" && (
              <div className="inline-block px-2.5 py-0.5 rounded-full bg-muted text-muted-foreground text-[11px] font-[500] w-fit">
                Target: {target}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CallQualityMetricCard;
